import React, { useState } from 'react';
import { toast } from 'sonner';
import { X, Save, Loader2 } from 'lucide-react';
import { notesApi } from '@/lib/notesApi';
import { extractError } from '@/lib/errors';
import { JewelleryMultiPicker } from './JewelleryMultiPicker';
import { SalesPicker } from '@/lib/salesPersons';
import { usePickBrands, BrandPick, commonBrand } from './TagBrandBits';

const LIMIT = 30;
const SELECT = 'h-9 rounded-lg border border-input bg-background px-2 text-sm';

// Create / edit a saved list by keying in jewellery numbers (desktop).
export function TagListBuilder({ items, editList, initialItems, onDone, onCancel }) {
  const [name, setName] = useState(editList?.name || '');
  const [picked, setPicked] = useState(editList?.items || initialItems || []); // [{ id, jewellery_no }]
  const [rmMode, setRmMode] = useState(editList?.rm_mode || 'expanded');
  const [showPieces, setShowPieces] = useState(editList ? editList.show_pieces : true);
  const [saving, setSaving] = useState(false);
  const [sales, setSales] = useState(editList?.sales_ids || []);
  const pickBrands = usePickBrands();
  const [brandPick, setBrandPick] = useState(editList?.brand_id || '');
  const byId = new Map((items || []).map((j) => [j.id, j]));
  const brandId = brandPick || commonBrand(picked.map((x) => byId.get(x.id)));
  const add = (list) => {
    const room = LIMIT - picked.length;
    if (list.length > room) toast.error(`A list can hold up to ${LIMIT} pieces — added the first ${Math.max(room, 0)}.`);
    setPicked((p) => [...p, ...list.slice(0, Math.max(room, 0)).map((it) => ({ id: it.id, jewellery_no: it.jewellery_no }))]);
  };
  const save = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Give the list a name.'); return; }
    if (!picked.length) { toast.error('Add at least one piece.'); return; }
    if (pickBrands.length && !brandId) { toast.error('Choose the brand.'); return; }
    setSaving(true);
    const body = { name: name.trim(), items: picked, rm_mode: rmMode, show_pieces: showPieces, rate_card_id: editList?.rate_card_id || '', sales_ids: sales, stone_rates: editList?.stone_rates || {}, brand_id: brandId };
    try {
      const { data } = editList ? await notesApi.put(`/notes/tag-workspace/tag-lists/${editList.id}`, body) : await notesApi.post('/notes/tag-workspace/tag-lists', body);
      toast.success(`List "${data.name}" saved.`); onDone(data);
    } catch (err) { toast.error(extractError(err, 'Could not save the list.')); } finally { setSaving(false); }
  };
  return (
    <form onSubmit={save} className="space-y-2.5 rounded-xl border border-[hsl(var(--ring))] bg-secondary/40 p-3" data-testid="tagws-list-builder">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-foreground">{editList ? `Edit pieces · ${editList.name}` : 'New list'} <span className="font-mono text-xs text-muted-foreground" data-testid="tagws-list-builder-count">{picked.length}/{LIMIT}</span></div>
        <button type="button" onClick={onCancel} aria-label="Cancel" className="text-muted-foreground" data-testid="tagws-list-builder-cancel"><X size={16} /></button>
      </div>
      <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder="List name (e.g. Mehta – 19 Oct)" className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm" data-testid="tagws-list-builder-name" />
      {pickBrands.length ? <BrandPick brands={pickBrands} value={brandId} onChange={setBrandPick} testid="tagws-list-builder-brand" /> : null}
      <JewelleryMultiPicker items={items} excludeIds={picked.map((x) => x.id)} onAdd={add} testid="tagws-list-builder-picker" />
      <div className="flex flex-wrap gap-1.5" data-testid="tagws-list-builder-chips">
        {picked.length ? picked.map((x) => (
          <span key={x.id} className="inline-flex items-center gap-1 rounded-full border border-border bg-card py-0.5 pl-2.5 pr-1 font-mono text-xs" data-testid="tagws-list-builder-chip">
            {x.jewellery_no}
            <button type="button" onClick={() => setPicked((p) => p.filter((y) => y.id !== x.id))} aria-label={`Remove ${x.jewellery_no}`} className="grid h-5 w-5 place-items-center text-muted-foreground hover:text-destructive" data-testid="tagws-list-builder-chip-remove"><X size={11} /></button>
          </span>
        )) : <span className="text-xs text-muted-foreground">Type a jewellery number, tick one or more pieces, then Add.</span>}
      </div>
      <div className="flex flex-wrap items-center justify-end gap-2">
        <div className="mr-auto w-full sm:w-64"><SalesPicker value={sales} onChange={setSales} up testid="tagws-list-builder-sales" /></div>
        <select value={rmMode} onChange={(e) => setRmMode(e.target.value)} className={SELECT} data-testid="tagws-list-builder-format">
          <option value="expanded">Expanded</option><option value="collapsed">Collapsed</option><option value="raw">Raw</option>
        </select>
        <select value={showPieces ? '1' : '0'} onChange={(e) => setShowPieces(e.target.value === '1')} className={SELECT} data-testid="tagws-list-builder-pieces">
          <option value="1">With pieces</option><option value="0">Without pieces</option>
        </select>
        <button type="submit" disabled={saving} className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-primary px-3 text-sm font-semibold text-primary-foreground disabled:opacity-50" data-testid="tagws-list-builder-save">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {editList ? 'Save changes' : 'Save list'}
        </button>
      </div>
    </form>
  );
}
